/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { createContext, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from './AuthContext';
import { hasPermission as checkPermission } from '../utils/permissions';

const PermissionContext = createContext(null);

// Public routes that don't require authentication
export const PUBLIC_ROUTES = ['/login', '/capture', '/error'];

const PERM_TEMPLATES_VIEW = 'templates.view';
const PERM_KNOWLEDGE_BASES_VIEW = 'knowledge_bases.view';
const PERM_AI_AGENTS_VIEW = 'ai_agents.view';

// Route permissions configuration
export const ROUTE_PERMISSIONS = {
  '/dashboard': { permissions: [], public: true }, // Always accessible
  '/workflows': { permissions: [PERM_TEMPLATES_VIEW] },
  '/workflow/new': { permissions: ['templates.create'] },
  '/workflow/:id': { permissions: [PERM_TEMPLATES_VIEW] },
  '/workflow/:id/edit': { permissions: ['templates.update'] },
  '/assignments': { permissions: ['assignments.view'] },
  '/assignments/new': { permissions: ['assignments.create'] },
  '/assignments/:id': { permissions: ['assignments.view_media'] },
  '/assignments/:id/edit': { permissions: ['assignments.update'] },
  '/accounts': { permissions: ['accounts.view'] },
  '/accounts/new': { permissions: ['accounts.create'] },
  '/accounts/:id/edit': { permissions: ['accounts.update'] },
  '/sub-accounts': { permissions: ['sub_accounts.view'] },
  '/sub-accounts/new': { permissions: ['sub_accounts.create'] },
  '/sub-accounts/:id/edit': { permissions: ['sub_accounts.update'] },
  '/sub-accounts/:id/clone': { permissions: ['sub_accounts.create'] },
  '/users': { permissions: ['users.view'] },
  '/users/new': { permissions: ['users.create'] },
  '/users/:id/edit': { permissions: ['users.update'] },
  '/roles': { permissions: ['roles.view'] },
  '/roles/new': { permissions: ['roles.create'] },
  '/roles/:id/edit': { permissions: ['roles.update'] },
  '/service-requests': { permissions: ['service_requests.view'] },
  '/service-requests/new': { permissions: ['service_requests.create'] },
  '/service-requests/:id': { permissions: ['service_requests.view'] },
  '/service-requests/:id/edit': { permissions: ['service_requests.edit'] },
  '/service-requests/types': { permissions: ['service_requests.create'] },
  '/service-requests/types/new': { permissions: ['service_requests.create'] },
  '/service-requests/types/:id/edit': { permissions: ['service_requests.create'] },
  '/media-library': { permissions: ['service_requests.view'] },
  '/knowledge-bases': { permissions: [PERM_KNOWLEDGE_BASES_VIEW] },
  '/knowledge-bases/new': { permissions: ['knowledge_bases.create'] },
  '/knowledge-bases/:id/documents': { permissions: [PERM_KNOWLEDGE_BASES_VIEW] },
  '/knowledge-bases/:id/documents/new': { permissions: ['knowledge_bases.upload'] },
  '/knowledge-bases/:id/documents/:documentId/edit': { permissions: ['knowledge_bases.upload'] },
  '/ai-agents': { permissions: [PERM_AI_AGENTS_VIEW] },
  '/ai-agents/new': { permissions: ['ai_agents.create'] },
  '/ai-agents/:id': { permissions: [PERM_AI_AGENTS_VIEW] },
  '/ai-agents/:id/edit': { permissions: ['ai_agents.update'] },
  '/webhooks': { permissions: ['webhooks.view'] },
  '/settings': { permissions: [] },
  '/appearance': { permissions: [], public: true }, // Always accessible
  '/help': { permissions: [], public: true }, // Always accessible
  '/profile': { permissions: [], public: true }, // Always accessible
};

/**
 * PermissionProvider - Global permission context that makes permission checking
 * available throughout the app without duplicating PermissionGuard in every file.
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - The app content
 */
export const PermissionProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();

  const value = useMemo(() => ({
        user,
        loading: authLoading,
        // Check if user has specific permissions
        hasPermission: (permissions, requireAll = false) => {
      if (authLoading) {
        return false;
      }

      if (!permissions || permissions.length === 0) {
        return true;
      }

      if (!user?.permissions || user.permissions.length === 0) {
        return false;
      }

      if (requireAll) {
      return permissions.every((permission) =>
        user.permissions.includes(permission)
      );
    }
    return permissions.some(p => user.permissions.includes(p));
   
   },
    // Check if user has any of the permissions in a list
    can: {
      // Account Management
      accounts: {
        view: () => checkPermission(user?.permissions || [], ['accounts.view']),
        create: () => checkPermission(user?.permissions || [], ['accounts.create']),
        update: () => checkPermission(user?.permissions || [], ['accounts.update']),
        delete: () => checkPermission(user?.permissions || [], ['accounts.delete']),
      },
      // Sub-Account Management
      sub_accounts: {
        view: () => checkPermission(user?.permissions || [], ['sub_accounts.view']),
        create: () => checkPermission(user?.permissions || [], ['sub_accounts.create']),
        update: () => checkPermission(user?.permissions || [], ['sub_accounts.update']),
        delete: () => checkPermission(user?.permissions || [], ['sub_accounts.delete']),
      },
      // User Management
      users: {
        view: () => checkPermission(user?.permissions || [], ['users.view']),
        create: () => checkPermission(user?.permissions || [], ['users.create']),
        update: () => checkPermission(user?.permissions || [], ['users.update']),
        delete: () => checkPermission(user?.permissions || [], ['users.delete']),
      },
      // Role Management
      roles: {
        view: () => checkPermission(user?.permissions || [], ['roles.view']),
        create: () => checkPermission(user?.permissions || [], ['roles.create']),
        update: () => checkPermission(user?.permissions || [], ['roles.update']),
        delete: () => checkPermission(user?.permissions || [], ['roles.delete']),
      },
      // Assignment Management
      assignments: {
        view: () => checkPermission(user?.permissions || [], ['assignments.view']),
        create: () => checkPermission(user?.permissions || [], ['assignments.create']),
        update: () => checkPermission(user?.permissions || [], ['assignments.update']),
        delete: () => checkPermission(user?.permissions || [], ['assignments.delete']),
        expire: () => checkPermission(user?.permissions || [], ['assignments.expire']),
        viewMedia: () => checkPermission(user?.permissions || [], ['assignments.view_media']),
        downloadMedia: () => checkPermission(user?.permissions || [], ['assignments.download_media']),
      },
      // Template/Workflow Management
      templates: {
        view: () => checkPermission(user?.permissions || [], [PERM_TEMPLATES_VIEW]),
        create: () => checkPermission(user?.permissions || [], ['templates.create']),
        update: () => checkPermission(user?.permissions || [], ['templates.update']),
        delete: () => checkPermission(user?.permissions || [], ['templates.delete']),
      },
      // Service Request Management
      service_requests: {
        view: () => checkPermission(user?.permissions || [], ['service_requests.view']),
        create: () => checkPermission(user?.permissions || [], ['service_requests.create']),
        edit: () => checkPermission(user?.permissions || [], ['service_requests.edit']),
        delete: () => checkPermission(user?.permissions || [], ['service_requests.delete']),
        deleteMedia: () => checkPermission(user?.permissions || [], ['service_requests.delete_media']),
      },
      // Work order checklists (reading them only needs service_requests.view)
      checklists: {
        create: () => checkPermission(user?.permissions || [], ['checklists.create']),
        rename: () => checkPermission(user?.permissions || [], ['checklists.rename']),
        delete: () => checkPermission(user?.permissions || [], ['checklists.delete']),
        itemAdd: () => checkPermission(user?.permissions || [], ['checklists.item_add']),
        itemEdit: () => checkPermission(user?.permissions || [], ['checklists.item_edit']),
        itemStatus: () => checkPermission(user?.permissions || [], ['checklists.item_status']),
        itemReorder: () => checkPermission(user?.permissions || [], ['checklists.item_reorder']),
        itemDelete: () => checkPermission(user?.permissions || [], ['checklists.item_delete']),
        commentAdd: () => checkPermission(user?.permissions || [], ['checklists.comment_add']),
        commentEdit: () => checkPermission(user?.permissions || [], ['checklists.comment_edit']),
        commentDelete: () => checkPermission(user?.permissions || [], ['checklists.comment_delete']),
      },
      // Knowledge Bases (AI Agent)
      knowledge_bases: {
        view: () => checkPermission(user?.permissions || [], [PERM_KNOWLEDGE_BASES_VIEW]),
        create: () => checkPermission(user?.permissions || [], ['knowledge_bases.create']),
        upload: () => checkPermission(user?.permissions || [], ['knowledge_bases.upload']),
        delete: () => checkPermission(user?.permissions || [], ['knowledge_bases.delete']),
      },
      // AI Agents
      ai_agents: {
        view: () => checkPermission(user?.permissions || [], [PERM_AI_AGENTS_VIEW]),
        create: () => checkPermission(user?.permissions || [], ['ai_agents.create']),
        update: () => checkPermission(user?.permissions || [], ['ai_agents.update']),
        delete: () => checkPermission(user?.permissions || [], ['ai_agents.delete']),
      },
      // Webhooks
      webhooks: {
        view: () => checkPermission(user?.permissions || [], ['webhooks.view']),
        create: () => checkPermission(user?.permissions || [], ['webhooks.create']),
        update: () => checkPermission(user?.permissions || [], ['webhooks.update']),
        delete: () => checkPermission(user?.permissions || [], ['webhooks.delete']),
      },
      // Settings
      settings: {
        manage: () => checkPermission(user?.permissions || [], ['settings.manage']),
      },
    },
    // Check if user can access a specific route.
    // If permissions are passed explicitly, use them directly (single source of truth).
    // If not, fall back to ROUTE_PERMISSIONS table lookup.
    canAccessRoute: (path, permissions = []) => {
      if (authLoading) { return false; }

      // Explicit permissions provided — use them directly, no table lookup needed
      if (permissions.length > 0) {
        return checkPermission(user?.permissions || [], permissions);
      }

      // No permissions provided — look up from ROUTE_PERMISSIONS table
      const routeConfig = Object.entries(ROUTE_PERMISSIONS).find(([pattern]) => {
        if (pattern === path) { return true; }
        // Handle dynamic routes like /workflow/:id
        const patternParts = pattern.split('/');
        const pathParts = path.split('/');
        if (patternParts.length !== pathParts.length) { return false; }
        return patternParts.every((part, i) => part === pathParts[i] || part.startsWith(':'));
      });

      if (!routeConfig) { return false; } // Deny access if route has no config

      const config = routeConfig[1];

      // Public routes are always accessible
      if (config.public) { return true; }

      return checkPermission(user?.permissions || [], config.permissions);
    },
    // Check if user has view-only access (no create/update/delete)
    isViewOnly: (resource) => {
      if (!user?.permissions) { return false; }
      
      const viewPermission = `${resource}.view`;
      const hasView = user.permissions.includes(viewPermission);
      const hasCreate = user.permissions.includes(`${resource}.create`);
      const hasUpdate = user.permissions.includes(`${resource}.update`);
      const hasDelete = user.permissions.includes(`${resource}.delete`);

      return hasView && !hasCreate && !hasUpdate && !hasDelete;
    },
  }), [user, authLoading]);

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

PermissionProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * usePermissions - Hook to access global permission checking
 * @returns {Object} Permission context with hasPermission, can, canAccessRoute, isViewOnly
 */
export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

/**
 * withPermissions - HOC to add permission checking to class components
 * @param {Component} WrappedComponent - The component to wrap
 * @returns {Component} Wrapped component with permissions context
 */
export const withPermissions = (WrappedComponent) => {
  return function WithPermissions(props) {
    const permissions = usePermissions();
    return <WrappedComponent {...props} permissions={permissions} />;
  };
};
